import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function AnnouncementHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'ai', 'fallback'

  useEffect(() => {
    fetchHistory();
    
    if (autoRefresh) {
      const interval = setInterval(fetchHistory, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchHistory = async () => {
    try {
      const response = await api.get('/ai/announcements/history');
      setHistory(response.data.announcements || []);
      setError('');
    } catch (err) {
      setError('Fout bij ophalen announcement history');
      console.error('Error fetching announcement history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = filter === 'all' 
    ? history 
    : history.filter(item => item.method.toLowerCase() === filter);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'net nu';
    if (diffMins < 60) return `${diffMins} min geleden`;
    if (diffHours < 24) return `${diffHours} uur geleden`;
    
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="admin-section"><p>Laden...</p></div>;
  }

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>🎙️ AI Announcements Geschiedenis</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <label style={{ fontWeight: 'bold' }}>Filter:</label>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '8px 16px',
            background: filter === 'all' ? '#007bff' : '#f0f0f0',
            color: filter === 'all' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Alles ({history.length})
        </button>
        <button
          onClick={() => setFilter('ai')}
          style={{
            padding: '8px 16px',
            background: filter === 'ai' ? '#28a745' : '#f0f0f0',
            color: filter === 'ai' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🤖 AI ({history.filter(h => h.method === 'AI').length})
        </button>
        <button
          onClick={() => setFilter('fallback')}
          style={{
            padding: '8px 16px',
            background: filter === 'fallback' ? '#ffc107' : '#f0f0f0',
            color: filter === 'fallback' ? '#333' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          💡 Fallback ({history.filter(h => h.method === 'Fallback').length})
        </button>
      </div>

      {filteredHistory.length === 0 ? (
        <div className="info-box" style={{ textAlign: 'center', padding: '40px', background: '#f0f0f0', borderRadius: '8px' }}>
          <p style={{ fontSize: '48px', margin: '0 0 10px 0' }}>📭</p>
          <h3>Geen announcements</h3>
          <p>Er zijn nog geen announcements gegenereerd.</p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Tijd</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Track</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Announcement</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Lyrics</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((item, index) => (
                <tr 
                  key={item.id || index}
                  style={{ 
                    borderBottom: '1px solid #dee2e6',
                    background: index % 2 === 0 ? 'white' : '#f8f9fa',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                  onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#f8f9fa'}
                >
                  <td style={{ padding: '12px', fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                    {formatTimestamp(item.timestamp)}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.title}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', lineHeight: '1.5', maxWidth: '500px' }}>
                    <span style={{ 
                      fontStyle: 'italic', 
                      color: '#333',
                      display: 'block',
                      padding: '8px',
                      background: item.method === 'AI' ? '#e8f5e9' : '#fff8e1',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${item.method === 'AI' ? '#4caf50' : '#ffc107'}`
                    }}>
                      "{item.announcement}"
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: item.method === 'AI' ? '#28a745' : '#ffc107',
                      color: item.method === 'AI' ? 'white' : '#333'
                    }}>
                      {item.method === 'AI' ? '🤖 AI' : '💡 Fallback'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '20px' }}>
                    {item.hasLyrics ? '📝' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#666' }}>
        <strong>Legenda:</strong> 
        <span style={{ marginLeft: '15px' }}>🤖 AI = Gegenereerd door TinyLlama</span>
        <span style={{ marginLeft: '15px' }}>💡 Fallback = Template (AI niet beschikbaar)</span>
        <span style={{ marginLeft: '15px' }}>📝 = Songteksten gebruikt voor context</span>
        <div style={{ marginTop: '8px' }}>
          Laatste <strong>{history.length}</strong> announcements worden bewaard (max 50)
        </div>
      </div>
    </div>
  );
}

export default AnnouncementHistory;
